import { useEffect, useRef } from 'react';
import { useAuth } from '../auth/AuthContext';
import { isAnalyticsSuppressed } from '../analytics';
import { logSessionEngagement } from './storefrontEvents';

const MILESTONES = [3, 10];

/** Track active storefront time and report dwell seconds by browser session. */
export default function StorefrontEngagementTracker() {
  const { token } = useAuth();
  const activeSecRef = useRef(0);
  const lastSentRef = useRef(0);
  const sentMilestonesRef = useRef(new Set());
  const visibleRef = useRef(typeof document !== 'undefined' ? !document.hidden : true);
  const tickStartRef = useRef(Date.now());

  useEffect(() => {
    if (isAnalyticsSuppressed()) return undefined;

    const send = (seconds) => {
      const sec = Math.floor(seconds);
      if (sec <= lastSentRef.current) return;
      lastSentRef.current = sec;
      logSessionEngagement({ seconds: sec, authToken: token || null });
    };

    const flushActive = () => {
      if (visibleRef.current) {
        const now = Date.now();
        activeSecRef.current += (now - tickStartRef.current) / 1000;
        tickStartRef.current = now;
      }
      return activeSecRef.current;
    };

    const checkMilestones = () => {
      const total = flushActive();
      MILESTONES.forEach((m) => {
        if (total >= m && !sentMilestonesRef.current.has(m)) {
          sentMilestonesRef.current.add(m);
          send(total);
        }
      });
    };

    const onVisibility = () => {
      if (document.hidden) {
        send(flushActive());
        visibleRef.current = false;
      } else {
        visibleRef.current = true;
        tickStartRef.current = Date.now();
      }
    };

    const onPageHide = () => {
      send(flushActive());
    };

    const interval = window.setInterval(checkMilestones, 1000);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      onPageHide();
    };
  }, [token]);

  return null;
}
